import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, CloudUploadOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { createDataObjectsUploadJobRequest } from "store/dataObjects/actions";
import { prependHeaderRow, syntheticColumnName } from "../csv";
import { buildToolError } from "./errorEnvelope";
import type { ToolPartProps } from "./types";

interface MappingEntry {
    csvColumnIndex: number;
    csvHeader?: string;
    schemaFieldName: string | null;
    reasoning?: string;
}

interface MapInput {
    hasHeader: boolean;
    description: string;
    uploadOption?: "APPEND_DATA" | "REPLACE_DATA";
    mappings: MappingEntry[];
}

interface MapOutput {
    success: boolean;
    cancelled?: boolean;
    error?: string;
    mappedCount?: number;
    skippedCount?: number;
    fileName?: string;
}

type Phase = "ready" | "no-csv" | "no-context" | "denied" | "uploading" | "uploaded" | "upload-error";

export default function MapDataObjectsCsvTool({ toolCallId, state, input, output, errorText, helpers }: ToolPartProps<MapInput, MapOutput>) {
    const { dispatch, addToolOutput, attachedCsv, schemaFields, dataSet, schemaId, businessAreaId, permission, mode, bulkUploadResult, onUploadStarted } = helpers;

    const [phase, setPhase] = useState<Phase>("ready");
    const [denyReason, setDenyReason] = useState<string | null>(null);
    const initialisedRef = useRef(false);
    const resolvedRef = useRef(false);
    // Snapshot of bulkUploadResult at dispatch; ignore stale prior results.
    const resultAtDispatchRef = useRef<unknown>(null);

    const resolvedMappings = useMemo(() => {
        if (!Array.isArray(input?.mappings)) return [];
        const fieldByName = new Map(schemaFields.map((f) => [(f?.name || "").toLowerCase(), f]));
        return input!.mappings.map((m) => {
            const sf = m.schemaFieldName ? fieldByName.get(m.schemaFieldName.toLowerCase()) : undefined;
            return {
                ...m,
                resolvedField: sf,
                unknownField: !!m.schemaFieldName && !sf,
            };
        });
    }, [input, schemaFields]);

    const mappedCount = resolvedMappings.filter((m) => !!m.resolvedField).length;
    const skippedCount = resolvedMappings.length - mappedCount;
    const unknownNames = resolvedMappings.filter((m) => m.unknownField).map((m) => m.schemaFieldName!);

    useEffect(() => {
        if (initialisedRef.current) return;
        if (state !== "input-available") return;
        initialisedRef.current = true;
        if (mode === "view" || !permission?.canAdd) {
            setPhase("denied");
            setDenyReason("You don't have permission to add records to this data set (canAdd is false or you're in read-only mode).");
            return;
        }
        if (!attachedCsv) {
            setPhase("no-csv");
            setDenyReason("The CSV is no longer attached to this chat. Re-attach the file and try again.");
            return;
        }
        if (!dataSet?.dataSetId || !schemaId || !businessAreaId) {
            setPhase("no-context");
            setDenyReason("Data set / schema context is incomplete.");
            return;
        }
    }, [state, attachedCsv, dataSet, schemaId, businessAreaId, permission, mode]);

    useEffect(() => {
        if (resolvedRef.current) return;
        if (state === "output-available" || state === "output-error") return;
        if (phase === "denied" || phase === "no-csv" || phase === "no-context") {
            resolvedRef.current = true;
            addToolOutput({
                tool: "mapDataObjectsCsv",
                toolCallId,
                output: { success: false, error: denyReason || "Cannot start bulk upload." },
            });
        }
    }, [phase, denyReason, state, toolCallId, addToolOutput]);

    useEffect(() => {
        if (phase !== "uploading") return;
        if (!bulkUploadResult) return;
        if (bulkUploadResult === resultAtDispatchRef.current) return;
        if (bulkUploadResult.success === true) {
            setPhase("uploaded");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "mapDataObjectsCsv",
                    toolCallId,
                    output: {
                        success: true,
                        mappedCount,
                        skippedCount,
                        fileName: attachedCsv?.fileName,
                    },
                });
            }
        } else if (bulkUploadResult.success === false) {
            setPhase("upload-error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "mapDataObjectsCsv",
                    toolCallId,
                    output: buildToolError(bulkUploadResult, "Bulk upload failed."),
                });
            }
        }
    }, [bulkUploadResult, phase, attachedCsv, mappedCount, skippedCount, toolCallId, addToolOutput]);

    const handleConfirm = async () => {
        if (!attachedCsv || !dataSet || !schemaId || !businessAreaId) return;
        if (mappedCount === 0) {
            setPhase("upload-error");
            if (!resolvedRef.current) {
                resolvedRef.current = true;
                addToolOutput({
                    tool: "mapDataObjectsCsv",
                    toolCallId,
                    output: { success: false, error: "No CSV columns are mapped to schema fields — nothing to upload." },
                });
            }
            return;
        }

        // Header names for the uploaded file, indexed by PHYSICAL csv column
        // position (0..columnCount-1) so they line up with the real file
        // columns regardless of the order/completeness of the LLM's `mappings`
        // array — both consumers below (the prepended header row and the
        // per-field `value`/`sourceColumns`) read this by csvColumnIndex.
        // When the CSV already has its own headers we keep them (so
        // `sourceColumns` references the original names). When it doesn't, we
        // synthesise a header row: mapped columns get their schema field name,
        // unmapped columns get a "Column N" placeholder so the CSV stays
        // well-formed.
        let fileToUpload: File = attachedCsv.file;
        const columnCount = Math.max(attachedCsv.columnCount || 0, ...resolvedMappings.map((m) => m.csvColumnIndex + 1));
        const mappingByColumn = new Map(resolvedMappings.map((m) => [m.csvColumnIndex, m]));
        const sourceColumnByIndex: string[] = [];
        for (let c = 0; c < columnCount; c++) {
            const m = mappingByColumn.get(c);
            const name = input?.hasHeader ? m?.csvHeader : (m?.resolvedField?.name as string | undefined);
            sourceColumnByIndex[c] = name || syntheticColumnName(c);
        }
        if (!input?.hasHeader) {
            try {
                fileToUpload = await prependHeaderRow(attachedCsv.file, sourceColumnByIndex);
            } catch (e: any) {
                setPhase("upload-error");
                if (!resolvedRef.current) {
                    resolvedRef.current = true;
                    addToolOutput({
                        tool: "mapDataObjectsCsv",
                        toolCallId,
                        output: { success: false, error: e?.message || "Could not prepare the file for upload." },
                    });
                }
                return;
            }
        }

        const fieldMappings = resolvedMappings
            .filter((m) => !!m.resolvedField)
            .map((m) => ({
                mappingType: "DATA_COLUMN",
                value: sourceColumnByIndex[m.csvColumnIndex],
                fieldId: m.resolvedField!.fieldId,
                name: m.resolvedField!.name,
                dataType: m.resolvedField!.dataType,
                sourceColumns: [sourceColumnByIndex[m.csvColumnIndex]],
            }));

        const uploadOption = input?.uploadOption || "APPEND_DATA";
        resultAtDispatchRef.current = bulkUploadResult;
        setPhase("uploading");
        dispatch(createDataObjectsUploadJobRequest(businessAreaId, schemaId, dataSet, uploadOption, fileToUpload, fieldMappings));
        onUploadStarted();
    };

    const handleCancel = () => {
        if (!resolvedRef.current) {
            resolvedRef.current = true;
            addToolOutput({ tool: "mapDataObjectsCsv", toolCallId, output: { success: false, cancelled: true } });
        }
    };

    return (
        <div className="rounded-2xl border border-[#001D3D]/40 bg-white p-3 text-sm text-zinc-800 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-[#001D3D]">
                <CloudUploadOutlined />
                <span className="text-xs font-semibold uppercase tracking-wider">Proposed CSV mapping</span>
            </div>
            <div className="text-base font-semibold text-zinc-900">{attachedCsv?.fileName || "(no file)"}</div>
            <div className="text-[10px] text-zinc-500">
                {input?.hasHeader ? "Headered CSV" : "Headerless CSV (a synthetic header row will be prepended)"} ·{" "}
                {mappedCount} mapped / {skippedCount} skipped · upload mode: {input?.uploadOption || "APPEND_DATA"}
            </div>
            {input?.description && <div className="mt-1 text-xs text-zinc-600">{input.description}</div>}

            {(phase === "denied" || phase === "no-csv" || phase === "no-context") && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {denyReason}
                </div>
            )}

            {phase !== "denied" && phase !== "no-csv" && phase !== "no-context" && resolvedMappings.length > 0 && (
                <div className="mt-2 overflow-hidden rounded-md border border-zinc-200">
                    <table className="w-full border-collapse text-left text-xs">
                        <thead className="bg-zinc-100 text-zinc-700">
                            <tr>
                                <th className="px-2 py-1 font-medium">#</th>
                                <th className="px-2 py-1 font-medium">CSV column</th>
                                <th className="px-2 py-1 font-medium">→ Schema field</th>
                                <th className="px-2 py-1 font-medium">Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {resolvedMappings.map((m) => (
                                <tr key={m.csvColumnIndex} className="border-t border-zinc-200">
                                    <td className="px-2 py-1 align-top text-zinc-500">{m.csvColumnIndex + 1}</td>
                                    <td className="px-2 py-1 align-top">
                                        <div className="font-medium text-zinc-900">{m.csvHeader || syntheticColumnName(m.csvColumnIndex)}</div>
                                        {m.reasoning && <div className="text-[10px] text-zinc-500">{m.reasoning}</div>}
                                    </td>
                                    <td className="px-2 py-1 align-top">
                                        {m.resolvedField ? (
                                            <span className="font-medium text-zinc-900">{m.resolvedField.name}</span>
                                        ) : m.unknownField ? (
                                            <span className="italic text-red-700">{m.schemaFieldName} (not in schema)</span>
                                        ) : (
                                            <span className="italic text-zinc-400">skipped</span>
                                        )}
                                    </td>
                                    <td className="px-2 py-1 align-top text-[10px] text-zinc-500">{m.resolvedField?.dataType || ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {unknownNames.length > 0 && phase === "ready" && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
                    <WarningOutlined className="mt-0.5" />
                    <span>
                        Unknown schema field(s): <span className="font-medium">{unknownNames.join(", ")}</span>. These columns will be skipped on upload.
                    </span>
                </div>
            )}

            {phase === "uploaded" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs text-green-700">
                    <CheckCircleOutlined /> Bulk upload job started.
                </div>
            )}
            {phase === "upload-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> Bulk upload could not be started.
                </div>
            )}
            {state === "output-available" && output?.cancelled && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs text-zinc-600">
                    <CloseCircleOutlined /> Cancelled.
                </div>
            )}
            {state === "output-error" && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                    <CloseCircleOutlined /> {errorText || "Tool error."}
                </div>
            )}

            {phase === "ready" && state !== "output-available" && state !== "output-error" && (
                <div className="mt-3 flex items-center justify-end gap-2">
                    <button type="button" onClick={handleCancel} className="rounded-md border border-zinc-300 bg-white px-3 py-1 text-xs text-zinc-700 hover:bg-zinc-50">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={mappedCount === 0}
                        className="rounded-md bg-[#001D3D] px-3 py-1 text-xs font-medium text-white hover:bg-[#002D5C] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Confirm & upload
                    </button>
                </div>
            )}
            {phase === "uploading" && (
                <div className="mt-3 flex items-center justify-end gap-2 text-xs text-zinc-500">
                    <LoadingOutlined spin /> Starting bulk upload...
                </div>
            )}
        </div>
    );
}
